import { useState } from 'react';
import { useParams, Link } from 'react-router';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { facilities, nearbyPlaces } from '../data/mockData';
import { useProperties } from '../contexts/PropertyContext';
import { 
  MapPin, Bed, Bath, Maximize, Calendar, Building2, Tag, 
  Phone, Mail, MessageCircle, ChevronLeft, ChevronRight,
  CheckCircle
} from 'lucide-react';
import { ImageWithFallback } from '../components/figma/ImageWithFallback';

const VideoPlayer = ({ url }: { url: string }) => {
  if (!url) return null;

  const getEmbedUrl = (url: string) => {
    // YouTube
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Vimeo
    const vimeoRegex = /(?:vimeo\.com\/)(\d+)/;
    const vimeoMatch = url.match(vimeoRegex);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return null;
  };

  const embedUrl = getEmbedUrl(url);

  if (embedUrl) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">فيديو العقار</h2>
        <div className="aspect-video w-full rounded-lg overflow-hidden shadow-md">
          <iframe
            src={embedUrl}
            title="Property Video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    );
  }

  // Direct video file
  if (url.match(/\.(mp4|webm|ogg)$/)) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4">فيديو العقار</h2>
        <div className="aspect-video w-full rounded-lg overflow-hidden shadow-md">
          <video controls className="w-full h-full">
            <source src={url} />
            Your browser does not support the video tag.
          </video>
        </div>
      </div>
    );
  }

  // Fallback for other URLs
  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4">فيديو العقار</h2>
      <a 
        href={url} 
        target="_blank" 
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 w-full p-4 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-800 transition-colors"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-video"><path d="m22 8-6 4 6 4V8Z"/><rect width="14" height="12" x="2" y="6" rx="2" ry="2"/></svg>
        <span>مشاهدة الفيديو على الموقع الأصلي</span>
      </a>
    </div>
  );
};

export default function PropertyDetails() {
  const { id } = useParams();
  const { properties } = useProperties();
  const property = properties.find((p: any) => p.id === id && p.active);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!property) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">العقار غير موجود</h1>
            <Link to="/properties">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-white">
                العودة للعقارات
              </Button>
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Use property's actual gallery images or fallback to mock images ONLY if no gallery images exist
  const galleryImages = property.galleryImages && property.galleryImages.length > 0 
    ? property.galleryImages 
    : property.mainImage 
      ? [] // If main image exists but no gallery, use empty array (don't show fallback images)
      : [ // Only show fallback images if no main image either
          `https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1200&h=800&fit=crop`,
          `https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&h=800&fit=crop`,
          `https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&h=800&fit=crop`,
          `https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=1200&h=800&fit=crop`,
        ];

  // Create display images array - prioritize main image first, then gallery images
  // Filter out blob URLs as they become invalid after page refresh
  const isValidImageUrl = (url: string) => {
    return url && !url.startsWith('blob:') && url.trim() !== '';
  };
  
  const mainImage = isValidImageUrl(property.mainImage) ? property.mainImage : null;
  const validGalleryImages = (property.galleryImages || []).filter(isValidImageUrl);
  const displayImages = mainImage 
    ? [mainImage, ...validGalleryImages]
    : validGalleryImages;

  // Debug: Log property data to see what we have
  console.log('Property data:', property);
  console.log('Main image:', property.mainImage);
  console.log('Gallery images:', property.galleryImages);
  console.log('Display images:', displayImages);

  const propertyFacilities = facilities.filter(f => property.facilities.includes(f.id));
  const propertyNearby = nearbyPlaces.filter(n => property.nearby.includes(n.id));

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('ar-EG', {
      style: 'currency',
      currency: 'EGP',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-[#D4AF37]">الرئيسية</Link>
            <span>/</span>
            <Link to="/properties" className="hover:text-[#D4AF37]">العقارات</Link>
            <span>/</span>
            <span className="text-gray-800">{property.title}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Image Gallery */}
            {displayImages.length > 0 ? (
              <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                <div className="relative h-96 md:h-[500px]">
                  <ImageWithFallback
                    src={displayImages[currentImageIndex]}
                    alt={property.title}
                    className="w-full h-full object-cover"
                  />
                
                  {/* Navigation Arrows */}
                  <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-800" />
                </button>
                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all"
                >
                  <ChevronRight className="w-6 h-6 text-gray-800" />
                </button>

                {/* Image Counter */}
                  <div className="absolute bottom-4 right-4 bg-black/70 text-white px-4 py-2 rounded-lg text-sm">
                    {`${currentImageIndex + 1} / ${displayImages.length}`}
                  </div>

                  {/* Badges */}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <span className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      property.type === 'rent' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-green-500 text-white'
                    }`}>
                      {property.type === 'rent' ? 'للإيجار' : 'للبيع'}
                    </span>
                    {property.featured && (
                      <span className="px-4 py-2 rounded-lg text-sm font-medium bg-[#FFE5A0] text-[#8B7109]">
                        مميز
                      </span>
                    )}
                  </div>
                </div>

                {/* Thumbnail Gallery */}
                <div className="flex gap-2 mt-4">
                  {displayImages.map((img: any, index: number) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-24 rounded-lg overflow-hidden ${
                        currentImageIndex === index ? 'ring-2 ring-[#D4AF37]' : ''
                      }`}
                    >
                      <ImageWithFallback
                        src={img}
                        alt={`صورة ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="text-center text-gray-500">
                  <div className="w-24 h-24 mx-auto mb-4 bg-gray-200 rounded-lg flex items-center justify-center">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <p>لا توجد صور متاحة لهذا العقار</p>
                </div>
              </div>
            )}

            {/* Property Details */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <div className="flex items-center gap-2 text-[#D4AF37] text-sm mb-3">
                <Tag className="w-4 h-4" />
                <span>{property.category}</span>
              </div>

              <h1 className="text-3xl font-bold text-gray-800 mb-4">{property.title}</h1>

              <div className="flex items-center gap-2 text-gray-600 mb-6">
                <MapPin className="w-5 h-5" />
                <span>{property.location}</span>
              </div>

              <div className="flex items-center justify-between mb-6 pb-6 border-b">
                <div className="text-4xl font-bold text-[#8B7109]">
                  {formatPrice(property.price)}
                </div>
                <div className="flex items-center gap-4 text-gray-600">
                  <div className="flex items-center gap-2">
                    <Bed className="w-5 h-5" />
                    <span>{property.bedrooms} غرفة</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Bath className="w-5 h-5" />
                    <span>{property.bathrooms} حمام</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Maximize className="w-5 h-5" />
                    <span>{property.area} م²</span>
                  </div>
                </div>
              </div>

              <h2 className="text-xl font-bold text-gray-800 mb-3">الوصف</h2>
              <p className="text-gray-600 leading-relaxed mb-6">{property.description}</p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <Building2 className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <div className="text-sm text-gray-600">الطابق</div>
                    <div className="font-bold text-gray-800">{property.floor}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <div className="text-sm text-gray-600">تاريخ النشر</div>
                    <div className="font-bold text-gray-800">
                      {new Date(property.createdAt).toLocaleDateString('ar-SA')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Facilities */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">المرافق</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {propertyFacilities.map(facility => (
                  <div key={facility.id} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span>{facility.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {property.videoUrl && <VideoPlayer url={property.videoUrl} />}

            {/* Maps Link */}
            {property.mapsUrl && (
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <a 
                  href={property.mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 w-full p-4 bg-[#4285F4]/10 hover:bg-[#4285F4]/20 border border-[#4285F4]/30 rounded-lg text-[#4285F4] transition-colors"
                >
                  <MapPin className="w-5 h-5" />
                  <span className="font-medium">عرض الموقع على Google Maps</span>
                </a>
              </div>
            )}

            {/* Nearby */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">الأماكن القريبة</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {propertyNearby.map(place => (
                  <div key={place.id} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-blue-500" />
                    <span>{place.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Map Section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4">الموقع</h2>
              {property.mapsUrl && property.mapsUrl.includes('/embed?') ? (
                <div className="w-full h-80 rounded-lg overflow-hidden">
                  <iframe
                    src={property.mapsUrl}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Property Location"
                  />
                </div>
              ) : property.mapsUrl ? (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 mb-2">رابط الموقع:</p>
                  <a 
                    href={property.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-[#4285F4] hover:underline"
                  >
                    <MapPin className="w-4 h-4" />
                    عرض على Google Maps
                  </a>
                  <p className="text-xs text-gray-500 mt-2">
                    لعرض الخريطة مباشرة هنا، استخدم رابط التضمين (Embed) من Google Maps
                  </p>
                </div>
              ) : (
                <div className="w-full h-80 bg-gray-200 rounded-lg flex items-center justify-center">
                  <div className="text-center text-gray-600">
                    <MapPin className="w-12 h-12 mx-auto mb-2" />
                    <p>خريطة الموقع</p>
                    <p className="text-sm">{property.location}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-xl font-bold text-gray-800 mb-4">اتصل بنا</h2>
              
              <div className="space-y-4">
                <Button className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-white h-12 text-lg">
                  <Phone className="w-5 h-5 ml-2" />
                  تصل الآن
                </Button>

                <Button variant="outline" className="w-full h-12 text-lg border-2">
                  <MessageCircle className="w-5 h-5 ml-2" />
                  واتساب
                </Button>

                <Button variant="outline" className="w-full h-12 text-lg border-2">
                  <Mail className="w-5 h-5 ml-2" />
                  إرسال بريد
                </Button>
              </div>

              <div className="mt-6 pt-6 border-t">
                <h3 className="font-bold text-gray-800 mb-4">احجز معاينة</h3>
                <form className="space-y-4">
                  <input
                    type="text"
                    placeholder="الاسم"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                  <input
                    type="tel"
                    placeholder="رقم الهاتف"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                  <input
                    type="email"
                    placeholder="البريد الإلكتروني"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  />
                  <textarea
                    placeholder="رسالة..."
                    rows={4}
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                  ></textarea>
                  <Button type="submit" className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-white h-12">
                    إرسال الطلب
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}